import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from "@reduxjs/toolkit";

import { reportsModelingService } from "../../services/reports-modeling-service";
import { REPORT_LEVELS, REPORT_TYPES, REPORT_VARIANT_TYPES } from "../../utils/sodReports.js";
import { BUSY_TYPES } from "../../utils/busy.js";
import {
    REPORT_MODELING_DELTA_STATUSES,
    getDeletedDefaultValuesForTcodes,
    REPORT_MODELING_CONFLICT_LEVELS_USER,
} from "../../utils/reports-modeling-utils";
import { getSelectedRoleReducers } from "./selectedRoleReducers";
import {
    getDefaultPermissionsValuesForTransactions,
    getTransactionFromRoleMenu,
    getPfcgTreeForTcodes,
    getTcodesMapCountFromMenu,
    getDeletedTcodesFromMenu,
    getTransactionsByIds,
    getDefaultPermissionsValuesForTransactionsMap,
} from "../../utils/role-menu";
import { recalcRoleTreeTrafficLightStatus } from "../../utils/reports-modeling-utils";
import { findItemInTree, TreeWalker, findFieldInTree } from "../../utils/tree-table.js";
import { removeGroupRoleChildren, addGroupRoleChildren } from "../../utils/group-role-utils.js";
import produce from "immer";
import {
    ModelingUser,
    UserModelingState,
    ModelingRole,
    UserRoleToAdd,
    RoleForView,
    RoleChildren,
    SaveVariantDataType,
    EmployeeDetailedVariant,
    EmployeeVariant,
} from "types/user-modeling/user-modeling-types";
import { AppDispatch, RootState } from "store";
import { ResponseError } from "types/common/responses/responses-types";
import { FetchTableDataParams } from "types/common/table/table-types";
import { ReportColumn } from "types/report/report-types";

const modelingUsersAdapter = createEntityAdapter<ModelingUser>();
const modelingRolesAdapter = createEntityAdapter<ModelingRole>();

const initialReportData = {
    canFetchResults: false,
    resultsOpen: false,
    reportLevel: REPORT_LEVELS.OPERATIONS,
    reportType: REPORT_TYPES.FINAL,
    conflictLevel: REPORT_MODELING_CONFLICT_LEVELS_USER.USER,
    rows: [],
    columns: [],
    total: 0,
    selectedIds: [],
    searchString: "",
    pageNumber: 1,
    limitNumber: 10,
    sortData: [],
    filterData: [],
    calculatedConflictLevels: [],
};

const initialState: UserModelingState["reportsModeling"]["userLevel"] = {
    parameters: {
        users: [],
        systems: [],
        matrixHeaders: [],
        risks: [],
        riskLevels: [],
        groups: null,
    },
    standardParameters: {
        users: [],
        systems: [],
        matrixHeaders: [],
        risks: [],
        riskLevels: [],
        groups: null,
    },

    parametersOpen: true,

    report: {
        ...initialReportData,
    },

    busy: false,
    busyType: null,
    pendingCount: 0,
    error: null,
    validationErrors: null,

    variantCreated: false,
    variants: [],
    currentVariant: null,
    shouldResetOnNavigate: true,

    modeling: {
        open: false,
        users: modelingUsersAdapter.getInitialState(),
        roles: modelingRolesAdapter.getInitialState(),
        selectedUserId: "",
        selectedRoleId: "",
        selectedUserRole: null,
    },
};

export const initModelingByVariant = createAsyncThunk<
    { variant: EmployeeDetailedVariant },
    { variant: EmployeeVariant },
    { rejectValue: ResponseError }
>("reportsModeling/userLevel/initModelingByVariant", async ({ variant }, { dispatch, rejectWithValue }) => {
    try {
        const detailedVariant = await reportsModelingService.getDetailedEmployeeVariant(variant);

        dispatch(setCurrentVariantFields(detailedVariant));
        dispatch(setOpenModeling());
        dispatch(clearReport());
        //dispatch(clearVariant());

        if (!detailedVariant) {
            throw Error("Varian is not defined");
        }

        return { variant: detailedVariant };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const initModelingManual = createAsyncThunk<ModelingUser[], void, { state: RootState; rejectValue: ResponseError | null }>(
    "reportsModeling/userLevel/fetchModelingUsers",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();

        if (state.reportsModeling.userLevel.parameters.users.length === 0) {
            return rejectWithValue(null);
        }

        // const users = await reportsModelingService.getModelingUsers(
        //     state.reportsModeling.userLevel.parameters.users,
        //     state.reportsModeling.userLevel.parameters.extendedSystems.map((item) => item.key),
        //     PERMISSIONS_TREE_VARIANT_LEVEL.USERS,
        //     currentVariantName
        // );
        try {
            const users = await reportsModelingService.getModelingEmployee(
                state.reportsModeling.userLevel.parameters.users[0],
                state.reportsModeling.userLevel.parameters.systems[0].key
            );
            return users;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const removeMenuItemFromTree = createAsyncThunk<void, { menuItemPath: string[]; id: string }, { state: RootState }>(
    "reportsModeling/userLevel/removeMenuItem",
    async ({ menuItemPath, id }, { getState, dispatch }) => {
        const state = getState();

        const selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const removedMenuItem = findItemInTree(menuItemPath, selectedRole.menu);

        const tcodesMapItem = getTcodesMapCountFromMenu([removedMenuItem]);

        dispatch(
            removeRoleMenuNode({
                id: selectedRole.id,
                activeNodePath: menuItemPath,
            })
        );

        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole.menu);

        const tcodeToValue = await getDefaultPermissionsValuesForTransactionsMap(selectedRole.systemId, Object.keys(tcodesMapMenu));

        const { deletedTcodes, remainedTcodes, deletedAtBranchTcodes } = getDeletedTcodesFromMenu(tcodesMapItem, tcodesMapMenu);

        try {
            const { orgLevelsForDelete, profilesForDelete } = getDeletedDefaultValuesForTcodes(
                deletedTcodes,
                remainedTcodes,
                tcodeToValue,
                deletedAtBranchTcodes
            );

            dispatch(
                substractPfcgTreeFromRole({
                    id,
                    orgLevelsForDelete,
                    profilesForDelete,
                    removedTcodes: Array.from(deletedTcodes),
                })
            );
        } catch (error) {
            console.log(error);
        }
    }
);

export const fetchReportColumns = createAsyncThunk<ReportColumn[], void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel/fetchReportColumns",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        try {
            const columns = await reportsModelingService.getUserLevelModelingColumns(
                state.reportsModeling.userLevel.report.reportLevel,
                state.reportsModeling.userLevel.report.reportType
            );
            return columns;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const fetchReportResults = createAsyncThunk<
    { rows: []; total: number },
    { params: FetchTableDataParams },
    { state: RootState; rejectValue: ResponseError }
>("reportsModeling/userLevel/fetchReportResults", async ({ params }, { getState, rejectWithValue }) => {
    const state = getState();

    if (!state.reportsModeling.userLevel.report.canFetchResults) {
        throw new Error("Can't fetch results");
    }

    const conflictLevel = state.reportsModeling.userLevel.report.conflictLevel;

    const newParams = {
        ...params,
        report_level: state.reportsModeling.userLevel.report.reportLevel,
        report_type: state.reportsModeling.userLevel.report.reportType,
        conflict_level: conflictLevel,
    };
    try {
        const response = await reportsModelingService.getUserLevelReportResults(newParams);
        return response as { rows: []; total: number };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

const createRoleForModeling = (role: RoleForView | RoleChildren, additionalProps = {}): ModelingRole => ({
    id: role.role,
    role: role.role,
    systemId: role.systemId,
    initialized: false,
    isComplex: role.isComplex,
    description: role.description ?? "",
    menu: [],
    tree: [],
    orgLevels: {},
    children: [],
    delta: [],
    orgLevelsDelta: [],
    manual: role.manual || false,
    profile: role.profile || role.role,
    isStandard: role.isStandard,
    open: false,
    added: role.added || false,
    ...additionalProps,
});

export const calcReport = createAsyncThunk<void, { conflictLevel: string; calcWithReset: boolean }, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel//calcReport",
    async ({ conflictLevel, calcWithReset }, { getState, rejectWithValue }) => {
        const state = getState();
        const alreadyCalculated = state.reportsModeling.userLevel.report.calculatedConflictLevels.includes(conflictLevel);

        if (alreadyCalculated && !calcWithReset) {
            return;
        }
        const currentVariant = state.reportsModeling.userLevel.currentVariant;
        const calcUsers = Object.values(state.reportsModeling.userLevel.modeling.users.entities);
        const changedUsers = calcUsers.filter((user) => user?.roles.some((role) => role.status === "a" || "d"));
        const modelingRoles = Object.values(state.reportsModeling.userLevel.modeling.roles.entities).filter((role) => !!role && role.changed);
        const rolesForView = calcUsers[0]?.roles;
        const groupRoles = calcUsers[0]?.roles.filter((role) => role.isComplex);
        if (!groupRoles || !rolesForView) return;

        const singleModelingRoles = modelingRoles.filter((role): role is ModelingRole => role != null && !role.isComplex);

        const parsedModelingRoles = reportsModelingService.parseModelingRolesVariantForServer(singleModelingRoles, rolesForView);
        const parsedGroupRoles = reportsModelingService.parseModelingGroupRolesForServer(groupRoles?.filter((role) => !role.isStandard));
        const parsedEmployee = changedUsers
            .filter((employee): employee is ModelingUser => employee != null)
            .map((employee) => reportsModelingService.parseModelingEmployeeForServer(employee));

        const systems = state.reportsModeling.userLevel.parameters.systems.map((item) => item.key);

        const form = {
            users: state.reportsModeling.userLevel.parameters.users,
            systems: systems,
            matrix_headers: state.reportsModeling.userLevel.parameters.matrixHeaders.map((item) => item.key),
            risks: state.reportsModeling.userLevel.parameters.risks.map((item) => item.key),
            risk_levels: state.reportsModeling.userLevel.parameters.riskLevels.map((item) => item.key),
            groups: [],
            changed_roles: parsedModelingRoles,
            changed_group_roles: parsedGroupRoles,
            variant_id: currentVariant ? currentVariant.id : null,
            all_users: parsedEmployee,
        };

        try {
            await reportsModelingService.calcUserLevelModelingReport(form, conflictLevel);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const calcCompareReport = createAsyncThunk<void, void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel//calcCompareReport",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const currentVariant = state.reportsModeling.userLevel.currentVariant;
        const calcUsers = Object.values(state.reportsModeling.userLevel.modeling.users.entities);
        const changedUsers = calcUsers.filter((user) => user?.roles.some((role) => role.status === "a" || "d"));
        const modelingRoles = Object.values(state.reportsModeling.userLevel.modeling.roles.entities).filter((role) => !!role && role.changed);
        const rolesForView = calcUsers[0]?.roles;
        const groupRoles = calcUsers[0]?.roles.filter((role) => role.isComplex);
        if (!groupRoles || !rolesForView) return;

        const singleModelingRoles = modelingRoles.filter((role): role is ModelingRole => role != null && !role.isComplex);

        const parsedModelingRoles = reportsModelingService.parseModelingRolesVariantForServer(singleModelingRoles, rolesForView);
        const parsedGroupRoles = reportsModelingService.parseModelingGroupRolesForServer(groupRoles?.filter((role) => !role.isStandard));
        const parsedEmployee = changedUsers
            .filter((employee): employee is ModelingUser => employee != null)
            .map((employee) => reportsModelingService.parseModelingEmployeeForServer(employee));
        const systems = state.reportsModeling.userLevel.parameters.systems.map((item) => item.key);

        const form = {
            users: state.reportsModeling.userLevel.parameters.users,
            systems: systems,
            matrix_headers: state.reportsModeling.userLevel.parameters.matrixHeaders.map((item) => item.key),
            risks: state.reportsModeling.userLevel.parameters.risks.map((item) => item.key),
            risk_levels: state.reportsModeling.userLevel.parameters.riskLevels.map((item) => item.key),
            groups: [],
            changed_roles: parsedModelingRoles,
            changed_group_roles: parsedGroupRoles,
            variant_id: currentVariant ? currentVariant.id : null,
            all_users: parsedEmployee,
        };

        try {
            await reportsModelingService.calcUserLevelModelingCompareReport(form);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const saveVariant = createAsyncThunk<EmployeeDetailedVariant | null, string, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel/saveVariant",
    async (variantName, { getState, rejectWithValue, dispatch }) => {
        const state = getState();
        const modelingUsers = Object.values(state.reportsModeling.userLevel.modeling.users.entities);
        const variant = state.reportsModeling.userLevel.variants.find((variant) => variant.name === variantName);
        const currentVariant = state.reportsModeling.userLevel.currentVariant;
        const isVariantNew = !variant;
        const sourceVariantId = currentVariant && currentVariant.id === variant?.id ? null : currentVariant?.id;
        const modelingRoles = Object.values(state.reportsModeling.userLevel.modeling.roles.entities).filter((role) => !!role);
        const rolesForView = modelingUsers[0]?.roles || [];
        const requestData: SaveVariantDataType = {
            id: variant ? variant.id : null,
            variant_name: variantName,
            variant_type: REPORT_VARIANT_TYPES.USERS_MODELING,
            source_variant_id: sourceVariantId || null,
            variant_data: {
                users: state.reportsModeling.userLevel.parameters.users,
                systems: state.reportsModeling.userLevel.parameters.systems.map((item) => item.key),
                matrix_headers: state.reportsModeling.userLevel.parameters.matrixHeaders.map((item) => item.key),
                risks: state.reportsModeling.userLevel.parameters.risks.map((item) => item.key),
                risk_levels: state.reportsModeling.userLevel.parameters.riskLevels.map((item) => item.key),
            },
            roles: reportsModelingService.parseModelingRolesVariantForServer(
                modelingRoles.filter((role) => !role.isStandard),
                rolesForView?.filter((role) => !role.isStandard)
            ),
            all_users: modelingUsers
                .filter((employee): employee is ModelingUser => !!employee)
                .map((employee) => reportsModelingService.parseModelingEmployeeForServer(employee)),
        };

        let savedVariant: EmployeeDetailedVariant | null = null;
        try {
            savedVariant = await reportsModelingService.saveEmployeeModelingVariant(requestData, isVariantNew);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
        dispatch(initModelingByVariant({ variant: savedVariant }));

        return savedVariant;
    }
);

export const deleteVariant = createAsyncThunk<EmployeeVariant, string, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel/deleteVariant",
    async (variantName, { rejectWithValue, getState }) => {
        const state = getState();

        const variant = state.reportsModeling.userLevel.variants.find((variant) => variant.name === variantName);
        if (!variant) throw Error("Variant not found");
        try {
            await reportsModelingService.deleteEmployeeVariant(variant?.id);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }

        return variant;
    }
);

export const setFavoritesForVariant = createAsyncThunk<
    { variantName: string; inFavorites: boolean },
    { variant: EmployeeVariant; inFavorites: boolean },
    { rejectValue: ResponseError }
>("reportsModeling/userLevel/setFavoritesForVariant", async ({ variant, inFavorites }, { rejectWithValue }) => {
    const form = {
        variant_name: variant.name,
        variant_type: REPORT_VARIANT_TYPES.USERS_MODELING,
        in_favorites: inFavorites,
        variant_id: variant.id,
    };

    try {
        await reportsModelingService.setFavoritesForVariant(form);
        return { variantName: variant.name, inFavorites: inFavorites };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const fetchVariantsByUser = createAsyncThunk<EmployeeVariant[], string, { rejectValue: ResponseError }>(
    "reportsModeling/userLevel/fetchVariantsByUser",
    async (searchString, { rejectWithValue }) => {
        const params: { variant_type: string; search?: string } = {
            variant_type: REPORT_VARIANT_TYPES.USERS_MODELING,
        };

        if (searchString) {
            params.search = searchString;
        }
        try {
            const response = await reportsModelingService.getVariantsByUser(params);

            return response as EmployeeVariant[];
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const exportReport = createAsyncThunk<void, void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel/exportReport",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const conflictLevel = state.reportsModeling.userLevel.report.conflictLevel;

        const params = {
            report_level: state.reportsModeling.userLevel.report.reportLevel,
            report_type: state.reportsModeling.userLevel.report.reportType,
            conflict_level: conflictLevel,
        };

        try {
            await reportsModelingService.exportUserLevelReport(params);
        } catch (error) {
            rejectWithValue(error as ResponseError);
        }
    }
);

export const removeGroupRoleChildFromModeling = createAsyncThunk<void, { role: RoleForView }, { state: RootState; dispatch: AppDispatch }>(
    "reportsModeling/userLevel/removeGroupRoleChildFromModeling",
    async ({ role }, { getState, dispatch }) => {
        const state = getState();
        const users = selectModelingUsers(state);
        const updates: { id: string; changes: { roles: RoleForView[] } }[] = [];

        const userRoles = users[0]?.roles;
        const groupRole = userRoles.find((userRole) =>
            userRole.children.some((child) => child.parentRole === userRole.role && child.role == role.role)
        );

        users.forEach((user) => {
            const roleIndex = user.roles.findIndex((r) => r.id == groupRole?.id);
            if (roleIndex !== -1) {
                const updatedRole = {
                    ...user.roles[roleIndex],
                    changed: true,
                    children: removeGroupRoleChildren(role.id, user.roles[roleIndex].children),
                };

                const updatedRoles = user.roles
                    .map((r, index) => (index === roleIndex ? updatedRole : r))
                    .filter((userRole) => userRole.parentRole != role.parentRole || userRole.id != role.id);
                // updatedRoles = updatedRoles.filter(role => role.id != roleIdToRemove || !role.isChild)

                updates.push({
                    id: user.id,
                    changes: { roles: updatedRoles },
                });
            }
        });

        dispatch(updateUsers(updates));
    }
);

export const addGroupRoleChildToModeling = createAsyncThunk<void, { roleId: string; newChildRole: RoleChildren }, { state: RootState }>(
    "reportsModeling/userLevel/addGroupRoleChildToModeling",
    async ({ roleId, newChildRole }, { getState, dispatch }) => {
        const state = getState();
        const users = selectModelingUsers(state);
        const modelingRoles = selectModelingRoles(state);
        const userRoles = users[0]?.roles;
        const groupRole = userRoles.find((userRole) => userRole.role == roleId);

        let newChild = modelingRoles.find((r) => r.role === newChildRole.role);

        if (!newChild) {
            newChild = createRoleForModeling(newChildRole);
            dispatch(addModelingRole({ ...newChild, added: true }));
        }
        newChildRole.parentRole = groupRole?.role ?? "";
        newChildRole.changed = newChild.changed;

        const updates: { id: string; changes: { roles: RoleForView[] } }[] = [];

        users.forEach((user) => {
            const roleIndex = user.roles.findIndex((r) => r.role == roleId);
            if (roleIndex !== -1) {
                const updatedRole = {
                    ...user.roles[roleIndex],
                    changed: true,
                    open: false,
                    children: addGroupRoleChildren(newChildRole, newChildRole.role, user.roles[roleIndex].children),
                };

                const updatedRoles = user.roles.map((r, index) => (index === roleIndex ? updatedRole : r));

                updates.push({
                    id: user.id,
                    changes: { roles: updatedRoles },
                });
            }
        });

        if (updates.length > 0) {
            dispatch(updateUsers(updates));
            dispatch(
                updateModelingRole({
                    id: groupRole?.id,
                    changes: { changed: true },
                })
            );
        }

        // dispatch(addGroupRoleChild({
        //     id: roleId,
        //     newChildRole,
        //     newChildRoleId: newChild.id
        // }));
    }
);

export const findPermissionInTree = createAsyncThunk<void, { searchString: string }, { state: RootState }>(
    "reportsModeling/roleLevel/findPermissionInTree",
    async ({ searchString }, { getState, dispatch }) => {
        const state = getState();
        const roleId = state.reportsModeling.userLevel.modeling.selectedRoleId;
        if (!roleId) throw Error("Role id is not defined");
        const role = state.reportsModeling.userLevel.modeling.roles.entities[roleId];

        if (role && role.tree && role.tree.length) {
            const updatedTree = produce(role.tree, (tree) => {
                findFieldInTree(tree, searchString);
            });

            dispatch(
                updateModelingRole({
                    id: roleId,
                    changes: { tree: updatedTree },
                })
            );
        }
    }
);

export const setReportLevelWithColumns = (reportLevel) => (dispatch) => {
    dispatch(setReportLevel(reportLevel));
    dispatch(fetchReportColumns());
};

export const setReportTypeWithColumns = (reportType) => (dispatch) => {
    dispatch(setReportType(reportType));
    dispatch(fetchReportColumns());
};

export const saveReportColumns = createAsyncThunk<
    { columns: ReportColumn[]; reportLevel: string; reportType: string },
    { columns: ReportColumn[]; reportLevel: string; reportType: string }
>("reportsModeling/userLevel/saveReportColumns", async ({ columns, reportLevel, reportType }) => {
    await reportsModelingService.saveUserLevelModelingColumns(columns, reportLevel, reportType);
    return { columns, reportLevel, reportType };
});

export const setModelingSelectedUserById = (id) => (dispatch, getState) => {
    const state = getState();
    const user = selectModelingUserById(state, id);

    dispatch(setModelingSelectedUserId(user?.id ?? ""));
};

export const setModelingSelectedUserByName =
    (name = "") =>
    (dispatch, getState) => {
        const state = getState();

        const lowerName = name.toLowerCase();

        const user = selectModelingUsers(state).find((user) => user.employee.toLowerCase() === lowerName);

        dispatch(setModelingSelectedUserId(user?.id ?? ""));
    };

export const addModelingSelectedUserRole = createAsyncThunk<
    RoleForView,
    UserRoleToAdd,
    { state: RootState; rejectValue: { useIntl: boolean; errorMessageId: string } }
>("reportsModeling/userLevel/addModelingSelectedUserRole", async (role, { getState, rejectWithValue }) => {
    const state = getState();
    const user = selectModelingSelectedUser(state);
    // const currentVariantName = selectCurrentVariantName(state);

    const userRoles = user?.roles;

    if (!userRoles) {
        return;
    }

    const userHasRole = userRoles.some((userRole) => userRole.role === role.role && userRole.systemId === role.systemId && !userRole?.isChild);

    if (userHasRole) {
        return rejectWithValue({
            useIntl: true,
            errorMessageId: "user-level-modeling.user-has-role-error",
        });
    }

    const roles = selectModelingRoles(state);
    let roleToAdd;
    if (roles.some((modelingRole) => modelingRole.role == role.role)) {
        roleToAdd = roles.find((modelingRole) => modelingRole.role == role.role);
    } else if (!role.isComplex) {
        roleToAdd = role;
        roleToAdd.id = role.role;
        roleToAdd.added = true;
        roleToAdd.children = [];
    } else {
        roleToAdd = await reportsModelingService.getDetailedIntegrationRole(role.role, role.systemId);
        roleToAdd.id = role.role;
        roleToAdd.added = true;
    }

    if (!roleToAdd) {
        return rejectWithValue({
            useIntl: true,
            errorMessageId: "user-level-modeling.role-doesnt-exist-error",
        });
    }

    return { ...roleToAdd };
});

export const restoreDeletedRole = createAsyncThunk<void, { roleId: string }, { state: RootState }>(
    "reportsModeling/userLevel/addOriginalUserRole",
    async ({ roleId }, { getState, dispatch }) => {
        const state = getState();
        const user = selectModelingSelectedUser(state);

        const updatedRoles = user?.roles.map((role) => (role.id === roleId && role?.status === "deleted" ? { ...role, status: "" } : role));

        dispatch(
            updateUsers([
                {
                    id: user?.id,
                    changes: { roles: updatedRoles },
                },
            ])
        );
    }
);

// export const createModelingSelectedUserRole = createAsyncThunk<
//     { role: string; description: string; manual: boolean; systemId: string; isComplex: boolean; profile: null; status: string },
//     { role: string; description: string; isComplex: boolean },
//     { state: RootState; rejectValue: { useIntl: boolean; errorMessageId: string } }
// >("reportsModeling/userLevel/createModelingSelectedUserRole", async ({ role, description, isComplex }, { getState, rejectWithValue }) => {
//     const state = getState();
//     const user = selectModelingSelectedUser(state);

//     if (!user) {
//         return;
//     }

//     const roles = selectModelingRoles(state);
//     const roleExists = await checkRoleExists(role, user.systemId, roles);

//     if (roleExists) {
//         return rejectWithValue({
//             useIntl: true,
//             errorMessageId: "user-level-modeling.role-exists-error",
//         });
//     } else {
//         try {
//             await reportsModelingService.createNewRole(role, description, user.systemId, isComplex);
//         } catch (error) {
//             return rejectWithValue({
//                 useIntl: true,
//                 errorMessageId: "user-level-modeling.role-creation-error",
//             });
//         }
//     }

//     return {
//         role,
//         description,
//         manual: true,
//         systemId: user.systemId,
//         isComplex,
//         profile: null,
//         status: REPORT_MODELING_DELTA_STATUSES.ADDED,
//     };
// });

export const setSelectedUserRole = createAsyncThunk<RoleForView, RoleForView>(
    "reportsModeling/userLevel/setSelectedUserRole",
    async (role, { dispatch }) => {
        dispatch(setModelingSelectedRoleById(role?.role));
        return role;
    }
);

export const setModelingSelectedRole = createAsyncThunk<ModelingRole | null, ModelingRole, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/userLevel/setModelingSelectedRole",
    async (role, { rejectWithValue, getState }) => {
        if (!role) return null;

        const modelingRoles = selectModelingRoles(getState());

        const currentModelingRole = modelingRoles.find((item) => item.role === role.role);

        if (currentModelingRole && currentModelingRole.initialized) {
            return currentModelingRole;
        }

        const state = getState();

        const currentVariant = state.reportsModeling.userLevel.currentVariant;

        if (!currentModelingRole) {
            throw Error("Current role is not defined");
        }

        let roleDetailed: ModelingRole | null = null;

        try {
            if (!currentVariant || currentModelingRole.isStandard || currentModelingRole.added) {
                roleDetailed = await reportsModelingService.getDetailedIntegrationRole(currentModelingRole.role, currentModelingRole.systemId);
            } else {
                roleDetailed = await reportsModelingService.getDetailedVariantRole(
                    currentVariant.id,
                    currentModelingRole.role,
                    currentModelingRole.systemId
                );
            }

            if (!roleDetailed) {
                throw Error("Role is not defined");
            }

            roleDetailed.id = currentModelingRole.id;
            roleDetailed.added = currentModelingRole.added;

            recalcRoleTreeTrafficLightStatus(roleDetailed?.tree, roleDetailed?.orgLevels);

            return roleDetailed;
        } catch (error) {
            rejectWithValue(error as ResponseError);
        }

        return null;
    }
);

export const setModelingSelectedRoleById = (id) => (dispatch, getState) => {
    const state = getState();

    const role = selectModelingRoleById(state, id);
    if (role) dispatch(setModelingSelectedRole(role));
};

export const setModelingSelectedRoleByName =
    (name = "") =>
    (dispatch, getState) => {
        const state = getState();

        const lowerName = name.toLowerCase();

        const role = selectModelingSelectedUserRoles(state)?.find((role) => role.role.toLowerCase() === lowerName);
        if (role) {
            dispatch(setModelingSelectedRole(role));
        }
    };

export const addDefaultPermissionsForMenuUpdate = createAsyncThunk<
    void,
    { id: string; transactionIds: string[] },
    { state: RootState; dispatch: AppDispatch }
>("reportsModeling/userLevel/addDefaultPermissionsForMenuUpdate", async ({ id, transactionIds }, { getState, dispatch }) => {
    const state = getState();
    const role = selectModelingRoleById(state, id);

    if (!role || !transactionIds?.length) {
        return;
    }

    dispatch(setBusy(true));

    await Promise.all(
        transactionIds.map(async (tcode) => {
            const newPermissionsTree = await getPfcgTreeForTcodes(role.systemId, [tcode]);

            const defaultValues = await getDefaultPermissionsValuesForTransactions(role.systemId, [tcode], null);

            dispatch(
                addModelingRoleTreeItems({
                    id,
                    items: newPermissionsTree,
                    defaultValues,
                    tcodes: transactionIds,
                    isStandard: true,
                })
            );
        })
    );

    dispatch(setBusy(false));
});

export const addRoleMenuNodeWithDefaultValues =
    ({ id, activeNodePath, tcodes }) =>
    async (dispatch, getState) => {
        let state = getState();
        let selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const transactions = await getTransactionsByIds(selectedRole.systemId, tcodes);

        dispatch(
            addRoleMenuNode({
                id,
                activeNodePath,
                tcodes,
                transactions,
            })
        );

        state = getState();
        selectedRole = selectModelingRoleById(state, id);

        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole?.menu);

        const tcodesForDefaultPermissions = tcodes.filter((tcode) => tcodesMapMenu[tcode] && tcodesMapMenu[tcode] > 0);

        if (tcodesForDefaultPermissions.length > 0) {
            dispatch(
                addDefaultPermissionsForMenuUpdate({
                    id,
                    transactionIds: tcodesForDefaultPermissions,
                })
            );
        }
    };

export const copyRoleMenuWithDefaultValues =
    ({ id, menuToCopy }) =>
    (dispatch) => {
        if (!menuToCopy?.length) {
            return;
        }

        dispatch(
            copyRoleMenu({
                id,
                menuToCopy,
            })
        );

        const transactionIds = getTransactionFromRoleMenu(menuToCopy[0].children);

        dispatch(
            addDefaultPermissionsForMenuUpdate({
                id,
                transactionIds,
            })
        );
    };

// export const setVariantWithFetchModeling = createAsyncThunk<void, EmployeeVariant, { dispatch: AppDispatch }>(
//     "reportsModeling/userLevel/setVariantWithFetchModeling",
//     async (variant, { dispatch }) => {
//         // dispatch(setCurrentVariantFields(variant));
//         // if (!variant) {
//         //     dispatch(clearVariant());
//         //     return;
//         // }
//         // await dispatch(initModelingByVariant(variant));
//     }
// );

export const setConflictLevelWithColumns = (conflictLevel) => (dispatch) => {
    dispatch(setConflictLevel(conflictLevel));
    dispatch(fetchReportColumns());
};

const selectedRoleReducers = getSelectedRoleReducers(
    (state) => state.modeling.roles,
    modelingRolesAdapter,
    (state, role) => {
        const users = Object.values(state.modeling.users.entities) as ModelingUser[];

        users.forEach((user) => {
            const changedRole = user.roles.find((item) => item.role === role.role);

            if (changedRole) {
                changedRole.changed = true;
                role.changed = true;
            }

            user.roles.forEach((r) => {
                if (r.isComplex) {
                    r.children.forEach((child) => {
                        if (child.role == role.role) {
                            child.changed = true;
                            role.changed = true;
                        }
                    });
                }
            });
        });
    },
    true
);

const userLevelSlice = createSlice({
    name: "reportsModeling/userLevel",
    initialState: initialState,
    reducers: {
        setUsers(state, action) {
            state.parameters.users = action.payload;
        },

        setBusy(state, action) {
            state.busy = action.payload;
        },

        updateUsers(state, action) {
            modelingUsersAdapter.updateMany(state.modeling.users, action.payload);
        },

        updateModelingRole: (state, action) => {
            modelingRolesAdapter.updateOne(state.modeling.roles, action.payload);
        },

        addModelingRole(state, action) {
            modelingRolesAdapter.addOne(state.modeling.roles, action.payload);
        },

        setSystems(state, action) {
            state.parameters.systems = action.payload;
        },

        setSystemsExtended(state, action) {
            state.parameters.systems = action.payload;
        },

        setMatrixHeaders(state, action) {
            state.parameters.matrixHeaders = action.payload;
        },

        setRisks(state, action) {
            state.parameters.risks = action.payload;
        },

        setRiskLevels(state, action) {
            state.parameters.riskLevels = action.payload;
        },

        setParametersOpen(state, action) {
            state.parametersOpen = action.payload;
        },

        setResultsOpen(state, action) {
            state.report.resultsOpen = action.payload;
        },

        setReportLevel(state, action) {
            state.report.reportLevel = action.payload;
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
        },

        setReportType(state, action) {
            state.report.reportType = action.payload;
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
        },

        setConflictLevel(state, action) {
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
            state.report.conflictLevel = action.payload;
        },

        setError(state, action) {
            state.error = action.payload;
        },

        setSelectedIds(state, action) {
            state.report.selectedIds = action.payload;
        },

        setSearchString(state, action) {
            if (state.report.searchString !== action.payload) {
                state.report.pageNumber = 1;
                state.report.searchString = action.payload;
            }
        },

        setPageNumber(state, action) {
            state.report.pageNumber = action.payload;
        },

        setLimitNumber(state, action) {
            state.report.pageNumber = 1;
            state.report.limitNumber = action.payload;
        },

        setSortData(state, action) {
            state.report.pageNumber = 1;
            state.report.sortData = action.payload;
        },

        setFilterData(state, action) {
            state.report.pageNumber = 1;
            state.report.filterData = action.payload;
        },

        setCurrentVariantFields(state, action) {
            // if (!state.currentVariant) {
            //     state.standardParameters.users = state.users;
            //     state.standardParameters.systems = state.systems;
            //     state.standardParameters.matrixHeaders = state.matrixHeaders;
            //     state.standardParameters.risks = state.risks;
            //     state.standardParameters.riskLevels = state.riskLevels;
            //     state.standardParameters.extendedSystems = state.extendedSystems
            // }

            state.standardParameters.users = state.parameters.users;
            state.standardParameters.systems = state.parameters.systems;
            state.standardParameters.matrixHeaders = state.parameters.matrixHeaders;
            state.standardParameters.risks = state.parameters.risks;
            state.standardParameters.riskLevels = state.parameters.riskLevels;
            state.standardParameters.systems = state.parameters.systems;

            const variant = action.payload;

            // const systemsCurrent =

            // const systemPart = systemsCurrent.map((system) => ({
            //     key: system.key,
            //     isGroup: false,
            //     text: system.text,
            // }));

            // const groupPart = groupsCurrent.map((group) => ({
            //     key: group.key,
            //     isGroup: false,
            //     text: group.text,
            // }));

            state.currentVariant = variant;

            state.parameters.users = variant ? variant.data.users : state.standardParameters.users;

            state.parameters.systems = variant ? variant.data.systems : state.standardParameters.systems;

            state.parameters.systems = variant ? variant.data.systems : state.standardParameters.systems;

            state.parameters.matrixHeaders = variant ? variant.data.matrixHeaders : state.standardParameters.matrixHeaders;

            state.parameters.risks = variant ? variant.data.risks : state.standardParameters.risks;

            state.parameters.riskLevels = variant ? variant.data.riskLevels : state.standardParameters.riskLevels;
        },

        clearFilters(state) {
            state.report.selectedIds = [];
            state.report.searchString = "";
            state.report.pageNumber = 1;
            state.report.sortData = [];
            state.report.filterData = [];
        },

        clearVariant(state) {
            state.modeling.users = modelingUsersAdapter.getInitialState();
            state.modeling.roles = modelingRolesAdapter.getInitialState();
            state.modeling.selectedUserId = "";
            state.modeling.selectedRoleId = "";
            state.currentVariant = null;
        },
        clearParameters(state) {
            state.parameters.matrixHeaders = [];
            state.parameters.riskLevels = [];
            state.parameters.risks = [];
            state.parameters.users = [];
            state.parameters.systems = [];
            state.parametersOpen = true;
            state.modeling.open = false;
        },

        setModelingSelectedUserId(state, action) {
            if (state.modeling.selectedUserId === action.payload?.id) {
                return;
            }

            state.modeling.selectedUserId = action.payload;
            state.modeling.selectedRoleId = "";
        },
        deleteModelingSelectedUserRole(state, action) {
            const user = state.modeling.users.entities[state.modeling.selectedUserId];
            const roleId = action.payload;

            if (!user || !roleId) {
                return;
            }

            const userRole = user.roles.find((role) => role.id == roleId);

            if (!userRole) {
                return;
            }

            const userRoleStatus = userRole.status;
            const userChanges: { roles: RoleForView[] } = { roles: [] };

            switch (userRoleStatus) {
                case REPORT_MODELING_DELTA_STATUSES.ADDED:
                    userChanges.roles = user.roles.filter((role) => role.id !== roleId);
                    break;

                case REPORT_MODELING_DELTA_STATUSES.DELETED:
                    userRole.status = "";
                    userChanges.roles = user.roles;
                    break;

                default:
                    userRole.status = REPORT_MODELING_DELTA_STATUSES.DELETED;
                    userChanges.roles = user.roles;
                    break;
            }

            state.modeling.selectedRoleId = null;

            modelingUsersAdapter.updateOne(state.modeling.users, {
                id: user.id,
                changes: userChanges,
            });
        },

        setModelingOpen(state, action) {
            state.modeling.open = action.payload;
        },

        expandGroupRole(state, action) {
            const selectedUserId = state.modeling.selectedUserId;
            const role = action.payload.role;
            const roles = state.modeling.users.entities[selectedUserId]?.roles;
            const groupRole = role?.isChild
                ? roles?.find((userRole) => role.parentRole == userRole.role)
                : roles?.find((userRole) => userRole.id == role.id);
            if (groupRole) {
                groupRole.open = !groupRole?.open;
            }
        },

        expandItemTree(state, action) {
            const roleId = state.modeling.selectedRoleId;
            if (!roleId) throw Error("Role id is null");
            const role = state.modeling.roles.entities[roleId];
            const item = findItemInTree(action.payload.path, role?.tree);
            item.open = !item.open;
            const treeWalker = new TreeWalker(item.children);
            treeWalker.walk(
                (node) => {
                    node.open = item.open;
                },
                () => {
                    return {
                        keyField: "",
                        keyValue: "",
                        childField: "children",
                    };
                },
                () => {}
            );
        },
        clearReport(state) {
            for (const resultsKey in initialReportData) {
                state.report[resultsKey] = initialReportData[resultsKey];
            }
        },
        setOpenModeling(state) {
            state.busy = false;
            state.parametersOpen = false;
            state.modeling.open = true;
        },
        setShouldResetOnNavigate(state, action) {
            state.shouldResetOnNavigate = action.payload;
        },

        ...selectedRoleReducers,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReportResults.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.report.rows = [];
                state.pendingCount += 1;
            })

            .addCase(fetchReportResults.rejected, (state, action) => {
                state.pendingCount -= 1;
                if (!state.pendingCount) {
                    state.busy = false;
                }
                const error = action.payload;
                if (!error) return;

                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })

            .addCase(fetchReportResults.fulfilled, (state, action) => {
                state.pendingCount -= 1;
                if (!state.pendingCount) {
                    state.busy = false;
                }
                state.error = null;
                state.validationErrors = null;

                if (action.payload) {
                    state.report.resultsOpen = true;
                    state.report.rows = action.payload.rows;
                    state.report.total = action.payload.total;
                }
            })

            .addCase(calcReport.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.error = null;
                state.validationErrors = null;
            })

            .addCase(calcReport.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })

            .addCase(calcReport.fulfilled, (state, action) => {
                state.busy = false;
                state.report.selectedIds = [];
                state.report.searchString = "";
                state.report.pageNumber = 1;
                state.report.sortData = [];
                state.report.filterData = [];
                state.report.canFetchResults = true;
                if (!action.meta.arg.calcWithReset) {
                    state.report.conflictLevel = action.meta.arg.conflictLevel;
                    const conflictLevel = action.meta.arg.conflictLevel;
                    if (!state.report.calculatedConflictLevels.includes(conflictLevel)) {
                        state.report.calculatedConflictLevels.push(conflictLevel);
                    }
                } else {
                    state.report.conflictLevel = REPORT_MODELING_CONFLICT_LEVELS_USER.USER;
                    state.report.calculatedConflictLevels = ["user"];
                }
            })

            .addCase(calcCompareReport.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.error = null;
                state.validationErrors = null;
            })

            .addCase(calcCompareReport.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                state.busy = false;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })

            .addCase(calcCompareReport.fulfilled, (state) => {
                state.report.selectedIds = [];
                state.report.searchString = "";
                state.report.pageNumber = 1;
                state.report.sortData = [];
                state.report.filterData = [];
                state.report.canFetchResults = true;
            })

            .addCase(fetchReportColumns.pending, (state) => {
                state.busy = true;
                state.pendingCount += 1;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(fetchReportColumns.fulfilled, (state, action) => {
                state.pendingCount -= 1;
                if (!state.pendingCount) {
                    state.busy = false;
                }
                state.report.columns = action.payload?.filter((column) => column.active);
            })

            .addCase(fetchReportColumns.rejected, (state, action) => {
                state.pendingCount -= 1;
                if (!state.pendingCount) {
                    state.busy = false;
                }
                if (!action.payload) return;
                state.error = action.payload;
            })

            .addCase(saveVariant.pending, (state) => {
                state.variantCreated = false;
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })

            .addCase(saveVariant.fulfilled, (state, action) => {
                state.busy = false;
                state.validationErrors = [];
                state.variantCreated = true;

                const savedVariant = action.payload;
                if (!savedVariant) throw Error("Variant is null");
                const index = state.variants.findIndex((variant) => variant.name === savedVariant?.name);

                if (index === -1) {
                    state.variants.push(savedVariant);
                } else {
                    state.variants[index] = savedVariant;
                }

                if (state.currentVariant?.name === savedVariant?.name) {
                    state.currentVariant = savedVariant;
                }
            })

            .addCase(saveVariant.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;

                if (!error) return;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })

            .addCase(deleteVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })

            .addCase(deleteVariant.fulfilled, (state, action) => {
                const deletedVariant = action.payload;

                state.busy = false;
                state.variants = state.variants.filter((variant) => variant.id !== deletedVariant.id);

                if (state.currentVariant?.id === deletedVariant.id) {
                    state.currentVariant = null;
                    state.parameters.users = [];
                    state.parameters.systems = [];
                    // state.parameters.extendedSystems = [];
                    state.parameters.matrixHeaders = [];
                    state.parameters.risks = [];
                    state.parameters.riskLevels = [];
                }
            })

            .addCase(deleteVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })

            .addCase(setFavoritesForVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })

            .addCase(setFavoritesForVariant.fulfilled, (state, action) => {
                const { variantName, inFavorites } = action.payload;

                state.busy = false;

                const variant = state.variants.find((variant) => variant.name === variantName);

                if (variant) {
                    variant.inFavorites = inFavorites;
                }

                if (state.currentVariant?.id === variant?.id && state.currentVariant) {
                    state.currentVariant.inFavorites = inFavorites;
                }
            })

            .addCase(setFavoritesForVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })

            .addCase(fetchVariantsByUser.fulfilled, (state, action) => {
                state.variants = action.payload;
            })

            .addCase(fetchVariantsByUser.rejected, (state, action) => {
                if (!action.payload) return;
                state.error = action.payload;
            })

            .addCase(exportReport.rejected, (state, action) => {
                if (!action.payload) return;
                state.error = action.payload;
            })

            .addCase(saveReportColumns.fulfilled, (state, action) => {
                const { columns, reportLevel, reportType } = action.payload;

                if (state.report.reportLevel === reportLevel && state.report.reportType === reportType) {
                    state.report.columns = columns.filter((column) => column.active);
                }
            })

            .addCase(initModelingManual.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(initModelingManual.fulfilled, (state, action) => {
                const users = action.payload;

                const roleMap = {};

                state.busy = false;
                state.parametersOpen = false;
                state.modeling.open = true;
                state.modeling.selectedRoleId = "";
                state.modeling.selectedUserId = "";
                state.currentVariant = null;

                for (const resultsKey in initialReportData) {
                    state[resultsKey] = initialReportData[resultsKey];
                }

                if (!users) {
                    return;
                }

                users?.forEach((user) =>
                    user.roles.forEach((role) => {
                        if (role.isComplex) {
                            role.open = false;

                            role.children.forEach((childRole) => {
                                const childRoleExtended = createRoleForModeling(childRole);
                                childRole.parentRole = role.role;
                                childRole.isChild = true;
                                roleMap[childRoleExtended.id] = childRoleExtended;
                            });
                        }

                        const roleExtended = createRoleForModeling(role);
                        roleMap[roleExtended.id] = roleExtended;
                    })
                );
                state.modeling.selectedUserRole = null;
                modelingUsersAdapter.setAll(state.modeling.users, users);
                modelingRolesAdapter.setAll(state.modeling.roles, Object.values(roleMap));
                state.shouldResetOnNavigate = false;
            })

            .addCase(initModelingManual.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(initModelingByVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(initModelingByVariant.fulfilled, (state, action) => {
                const { variant } = action.payload;
                const roleMap = {};

                const updatedEmployees = variant.employees.map((user) => ({
                    ...user,
                    roles: user.roles.map((role) => {
                        if (role.isComplex) {
                            const parentRoleExtended = createRoleForModeling(role);
                            roleMap[parentRoleExtended.id] = parentRoleExtended;

                            const updatedChildren = role.children.map((childRole) => {
                                const updatedChildRole = {
                                    ...childRole,
                                    parentRole: role.role,
                                    isChild: true,
                                };

                                const childRoleExtended = createRoleForModeling(updatedChildRole, {
                                    parentRole: role.role,
                                    isChild: true,
                                });
                                roleMap[childRoleExtended.id] = childRoleExtended;

                                return updatedChildRole;
                            });
                            const roleExtended = createRoleForModeling(role);
                            roleMap[roleExtended.id] = roleExtended;
                            return {
                                ...role,
                                children: updatedChildren,
                            };
                        }

                        const roleExtended = createRoleForModeling(role);
                        roleMap[roleExtended.id] = roleExtended;
                        return role;
                    }),
                }));

                state.modeling.selectedUserRole = null;
                state.modeling.selectedUserId = "";
                state.modeling.selectedRoleId = "";
                modelingUsersAdapter.setAll(state.modeling.users, updatedEmployees);
                modelingRolesAdapter.setAll(state.modeling.roles, Object.values(roleMap));

                state.currentVariant = {
                    ...variant,
                    employees: updatedEmployees,
                };

                state.busy = false;
            })
            .addCase(initModelingByVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(addModelingSelectedUserRole.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(addModelingSelectedUserRole.fulfilled, (state, action) => {
                const role = action.payload;
                const user = state.modeling.users.entities[state.modeling.selectedUserId];

                role.status = REPORT_MODELING_DELTA_STATUSES.ADDED;

                state.busy = false;

                const modelingRole = createRoleForModeling(role);
                const modelingRolesCurrent = Object.values(state.modeling.roles.entities);

                if (role.children.length) {
                    role.id = role.role;
                    modelingRole.initialized = true;
                    role.children.forEach((child) => {
                        const modelingRoleChildIndex = modelingRolesCurrent.findIndex(
                            (role) => role?.role === child.role && role?.systemId === child.systemId
                        );
                        child.isChild = true;
                        child.parentRole = role.role ?? "";
                        if (modelingRoleChildIndex != -1) {
                            if (!modelingRolesCurrent[modelingRoleChildIndex]) return;
                            child.id = modelingRolesCurrent[modelingRoleChildIndex].id;
                            // child = modelingRolesCurrent[modelingRoleChildIndex];
                        } else {
                            child.added = true;
                            const newChild = createRoleForModeling(child);
                            modelingRolesAdapter.addOne(state.modeling.roles, newChild);
                            child.id = newChild.id;
                        }
                    });
                } else {
                    role.isChild = false;
                    role.parentRole = "";
                }

                role.changed = false;

                const modelingRoleIndex = modelingRolesCurrent.findIndex(
                    (role) => role?.role === modelingRole.role && role?.systemId === modelingRole.systemId
                );

                if (modelingRoleIndex === -1) {
                    modelingRolesAdapter.addOne(state.modeling.roles, modelingRole);
                    role.id = modelingRole.id;
                } else {
                    const currentModelingRole = modelingRolesCurrent[modelingRoleIndex];
                    if (!currentModelingRole) throw Error("Role is not defined");
                    role.id = currentModelingRole.id;
                    role.changed = true;
                }
                if (user) {
                    modelingUsersAdapter.updateOne(state.modeling.users, {
                        id: user?.id,
                        changes: {
                            roles: [...user.roles, role],
                        },
                    });
                }
            })

            .addCase(addModelingSelectedUserRole.rejected, (state) => {
                state.busy = false;
            })

            // .addCase(createModelingSelectedUserRole.fulfilled, (state, action) => {
            //     const role = action.payload;

            //     const user = state.modeling.users.entities[state.modeling.selectedUserId];

            //     state.busy = false;

            //     const modelingRolesCurrent = Object.values(state.modeling.roles.entities);
            //     const modelingRole = createRoleForModeling(role);
            //     if (role && user) {
            //         role.id = modelingRole.id;
            //         role.changed = false;

            //         modelingUsersAdapter.updateOne(state.modeling.users, {
            //             id: user?.id,
            //             changes: {
            //                 roles: [...user.roles, role],
            //             },
            //         });
            //     }

            //     const modelingRoleIndex = modelingRolesCurrent.findIndex((role) => role?.role === modelingRole.role && role?.systemId === modelingRole.systemId);

            //     if (modelingRoleIndex === -1) {
            //         modelingRolesAdapter.addOne(state.modeling.roles, modelingRole);
            //     }
            // })

            .addCase(setSelectedUserRole.fulfilled, (state, action) => {
                state.modeling.selectedUserRole = action.payload;
            })

            .addCase(setModelingSelectedRole.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(setModelingSelectedRole.fulfilled, (state, action) => {
                const role = action.payload;

                if (!role) {
                    state.modeling.selectedRoleId = "";
                } else if (role.id in state.modeling.roles.entities) {
                    state.modeling.selectedRoleId = role.id;
                    modelingRolesAdapter.setOne(state.modeling.roles, role);
                }

                state.busy = false;
            })

            .addCase(setModelingSelectedRole.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            });
    },
});

export const {
    setUsers,
    setBusy,
    setSystems,
    setSystemsExtended,
    setMatrixHeaders,
    setRisks,
    setRiskLevels,
    setParametersOpen,
    setResultsOpen,
    setReportLevel,
    setReportType,
    setError,
    setSelectedIds,
    setSearchString,
    setPageNumber,
    setLimitNumber,
    setSortData,
    setFilterData,
    setCurrentVariantFields,
    clearFilters,
    setModelingSelectedUserId,
    deleteModelingSelectedUserRole,
    setModelingOpen,
    setModelingRoleTreeField,
    addModelingRoleTreeItems,
    setModelingRoleOrgLevelValues,
    openRoleMenuNode,
    addRoleMenuFolder,
    addRoleMenuNode,
    removeRoleMenuNode,
    copyRoleMenu,
    addGroupRoleChild,
    removeGroupRoleChild,
    removeModelingRoleTreeItem,
    substractPfcgTreeFromRole,
    clearVariant,
    setConflictLevel,
    expandItemTree,
    updateUsers,
    addModelingRole,
    expandGroupRole,
    updateModelingRole,
    clearReport,
    setOpenModeling,
    clearParameters,
    setShouldResetOnNavigate,
} = userLevelSlice.actions;

export const {
    selectIds: selectModelingUserIds,
    selectAll: selectModelingUsers,
    selectById: selectModelingUserById,
} = modelingUsersAdapter.getSelectors((state: RootState) => state.reportsModeling.userLevel.modeling.users);

export const {
    selectIds: selectModelingRoleIds,
    selectAll: selectModelingRoles,
    selectById: selectModelingRoleById,
} = modelingRolesAdapter.getSelectors((state: RootState) => state.reportsModeling.userLevel.modeling.roles);

export const selectModelingSystemId = (state: RootState) => {
    const systems = state.reportsModeling.userLevel.parameters.systems;

    return (systems && systems[0] && systems[0].key) || null;
};

export const selectModelingSelectedUserId = (state) => state.reportsModeling.userLevel.modeling.selectedUserId;
export const selectModelingSelectedUser = (state) => {
    const selectedUserId = selectModelingSelectedUserId(state);
    return selectModelingUserById(state, selectedUserId);
};
export const selectModelingSelectedUserSystemId = createSelector(selectModelingSelectedUser, (user) => user?.systemId);

export const selectModelingUsersBySearch = createSelector(
    selectModelingUsers,
    (_: RootState, searchString: string = "") => searchString.toLowerCase(),
    (users, searchString) => users.filter(({ employee }) => employee.toLowerCase().includes(searchString))
);

export const selectUserRole = (state) => state.reportsModeling.userLevel.modeling.selectedUserRole;
export const selectModelingSelectedRoleId = (state) => state.reportsModeling.userLevel.modeling.selectedRoleId;
export const selectModelingSelectedRole = (state) => {
    const selectedRoleId = selectModelingSelectedRoleId(state);
    if (!selectedRoleId) return null;
    return selectModelingRoleById(state, selectedRoleId);
};

export const selectModelingOpen = (state) => state.reportsModeling.userLevel.modeling.open;

export const selectModelingSelectedUserRoles = createSelector(
    selectModelingSelectedUser,
    selectModelingRoles,
    (_: RootState, searchString: string = "") => searchString.toLowerCase(),
    (user, roles, searchString) => {
        if (!user) return;
        const userRoleIds = user.roles.map((r) => r.id);

        return roles
            .filter((role) => userRoleIds.includes(role.id) && role.role.toLowerCase().includes(searchString))
            .map((role) => ({
                ...role,
                // status: user.roleStatuses[role.id] || role.status,
            }));
    }
);

export const selectModelingNewRoles = createSelector(
    [
        selectModelingRoles,
        (state: RootState, systemId: string, searchString: string = "") => ({ systemId, searchString: searchString.toLowerCase() }),
    ],
    (roles: ModelingRole[], params) =>
        roles.filter(
            (role) =>
                role.status === REPORT_MODELING_DELTA_STATUSES.ADDED &&
                role.systemId === params.systemId &&
                role.role.toLowerCase().includes(params.searchString)
        )
);

export const selectCurrentVariantName = (state) => state.reportsModeling.userLevel.currentVariant?.variantName;

export default userLevelSlice.reducer;
